import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import {
  isPushSupported,
  isCurrentlySubscribed,
  subscribeToPush,
  getPushPermission,
  isIOS,
  isStandalone,
} from '../lib/pushNotifications';

const STORAGE_KEY_DISMISSED = 'ept_push_prompt_dismissed_v2';
const STORAGE_KEY_LAST_SHOWN = 'ept_push_prompt_last_shown';

// Show the prompt at most once per 7 days so it nudges but doesn't annoy
const NAG_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('push'); // 'push' | 'ios-install'

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const check = async () => {
      const supported = await isPushSupported();

      // iPhone/iPad in Safari browser: push only works once the site is
      // installed on the Home Screen (iOS 16.4+). Guide the user there.
      if (!supported && isIOS() && !isStandalone()) {
        const dismissedPermanent = localStorage.getItem(STORAGE_KEY_DISMISSED) === '1';
        if (dismissedPermanent) return;
        const lastShown = parseInt(localStorage.getItem(STORAGE_KEY_LAST_SHOWN) || '0', 10);
        if (Date.now() - lastShown < NAG_INTERVAL_MS) return;
        // Don't stack on top of the InstallPrompt banner: show this nudge only
        // after the user has dismissed the install banner (24h snooze active)
        const pwaDismissedAt = parseInt(localStorage.getItem('pwa-dismissed-at') || '0', 10);
        const installBannerVisible = !(pwaDismissedAt && (Date.now() - pwaDismissedAt) < 24 * 60 * 60 * 1000);
        if (installBannerVisible) return;
        setTimeout(() => {
          if (!cancelled) {
            setMode('ios-install');
            setVisible(true);
          }
        }, 2500);
        return;
      }

      // Skip on unsupported browsers (e.g. incognito)
      if (!supported || cancelled) return;

      const permission = await getPushPermission();
      if (permission === 'denied') return; // user permanently blocked - respect it

      const already = await isCurrentlySubscribed();
      if (already || cancelled) return;

      // Respect a permanent dismissal OR the 7-day nag window
      const dismissedPermanent = localStorage.getItem(STORAGE_KEY_DISMISSED) === '1';
      if (dismissedPermanent) return;

      const lastShown = parseInt(localStorage.getItem(STORAGE_KEY_LAST_SHOWN) || '0', 10);
      if (Date.now() - lastShown < NAG_INTERVAL_MS) return;

      // Delay slightly so it doesn't flash at the first render
      setTimeout(() => {
        if (!cancelled) setVisible(true);
      }, 2500);
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const enable = async () => {
    setLoading(true);
    try {
      const sub = await subscribeToPush();
      if (sub) {
        toast.success(isItalian ? 'Notifiche push attivate!' : 'Push notifications enabled!');
        localStorage.setItem(STORAGE_KEY_DISMISSED, '1');
        setVisible(false);
      } else {
        toast.error(
          isItalian
            ? 'Permesso negato. Puoi attivarle in seguito dalle impostazioni del browser.'
            : 'Permission denied. You can enable them later from browser settings.',
        );
        localStorage.setItem(STORAGE_KEY_LAST_SHOWN, String(Date.now()));
        setVisible(false);
      }
    } catch (e) {
      toast.error(isItalian ? 'Errore attivazione push' : 'Push enablement error');
    } finally {
      setLoading(false);
    }
  };

  const later = () => {
    localStorage.setItem(STORAGE_KEY_LAST_SHOWN, String(Date.now()));
    setVisible(false);
  };

  const neverAgain = () => {
    localStorage.setItem(STORAGE_KEY_DISMISSED, '1');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:bottom-6 z-[70] max-w-sm ml-auto"
          data-testid="push-notification-prompt"
        >
          <div
            className="rounded-xl border border-[#00F0FF]/40 bg-[#0a0d15]/95 backdrop-blur-md p-4 shadow-[0_8px_32px_-8px_rgba(0,240,255,0.35)]"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 flex items-center justify-center">
                <BellRing size={18} className="text-[#00F0FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-heading uppercase tracking-wider text-sm mb-1">
                  {mode === 'ios-install'
                    ? (isItalian ? 'Notifiche su iPhone' : 'Notifications on iPhone')
                    : (isItalian ? 'Attiva le notifiche' : 'Enable notifications')}
                </h4>
                {mode === 'ios-install' ? (
                  <div className="text-xs text-slate-300 leading-relaxed space-y-1" data-testid="ios-install-instructions">
                    <p>{isItalian ? 'Per ricevere le notifiche sul telefono, installa EPT come app:' : 'To get notifications on your phone, install EPT as an app:'}</p>
                    <ol className="list-decimal list-inside space-y-0.5 text-slate-400">
                      <li>{isItalian ? <>Tocca <span className="text-[#00F0FF]">Condividi</span> in Safari</> : <>Tap <span className="text-[#00F0FF]">Share</span> in Safari</>}</li>
                      <li>{isItalian ? <>Scegli <span className="text-[#00F0FF]">"Aggiungi alla schermata Home"</span></> : <>Choose <span className="text-[#00F0FF]">"Add to Home Screen"</span></>}</li>
                      <li>{isItalian ? 'Apri l\'app EPT e attiva le notifiche' : 'Open the EPT app and enable notifications'}</li>
                    </ol>
                  </div>
                ) : (
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {isItalian
                      ? 'Ricevi avvisi immediati sul telefono per partite, report e chat — anche quando il sito è chiuso.'
                      : 'Get instant alerts on your phone for matches, reports and chat — even when the site is closed.'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={later}
                className="shrink-0 text-slate-500 hover:text-white"
                aria-label={isItalian ? 'Ricordamelo più tardi' : 'Remind me later'}
                data-testid="push-prompt-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              {mode === 'ios-install' ? (
                <button
                  type="button"
                  onClick={later}
                  className="flex-1 h-9 px-3 rounded-md bg-[#00F0FF] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#00F0FF]/85"
                  data-testid="push-prompt-ios-ok"
                >
                  {isItalian ? 'Ho capito' : 'Got it'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={enable}
                  disabled={loading}
                  className="flex-1 h-9 px-3 rounded-md bg-[#00F0FF] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#00F0FF]/85 disabled:opacity-60 flex items-center justify-center gap-1.5"
                  data-testid="push-prompt-enable"
                >
                  {loading ? (
                    <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <BellRing size={13} />
                  )}
                  {isItalian ? 'Attiva' : 'Enable'}
                </button>
              )}
              <button
                type="button"
                onClick={neverAgain}
                className="h-9 px-3 rounded-md border border-white/10 bg-white/5 text-slate-400 hover:text-slate-200 text-xs uppercase tracking-wider"
                data-testid="push-prompt-never"
              >
                {isItalian ? 'No, grazie' : 'No thanks'}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
